/* eslint-disable max-classes-per-file */

import { localStorage } from '@openclassrooms/utils/local-storage';

type ThreadIdList = {
  [key: string]: number;
};

const STORAGE_KEY = 'helpSessionThreadId';

export class HelpSessionTopic {
  version: number = 0;

  name: string;

  private values: Map<string, string> = new Map();

  constructor(name: string) {
    this.name = name;
  }

  setValue(name: string, value: string) {
    const currentValue = this.values.get(name);

    if (currentValue && currentValue === value) {
      return;
    }

    this.values.set(name, value);
    this.version += 1;
  }

  getValues() {
    return Object.fromEntries(this.values);
  }

  getId() {
    return `${this.name}-v${this.version}`;
  }

  getValueForAI(name: string) {
    return this.values.get(name);
  }
}

function cleanHtml(htmlString: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${htmlString}</body>`, 'text/html');
  const { body } = doc;

  const voidElements = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ]);

  function traverseAndClean(node: Element) {
    [...node.children].forEach((child) => traverseAndClean(child));

    const tagName = node.tagName.toLowerCase();
    if (tagName === 'img' || tagName === 'svg') {
      node.parentNode?.removeChild(node);
      return;
    }

    ['class', 'id', 'style', 'role'].forEach((attr) => node.removeAttribute(attr));

    const attributes = [...node.attributes];
    for (let i = 0; i < attributes.length; i += 1) {
      const attrName = attributes[i].name;
      if (attrName.startsWith('data-') || attrName.startsWith('aria-')) {
        node.removeAttribute(attrName);
      }
    }

    if (voidElements.has(tagName)) {
      return;
    }

    const hasChildElements = node.children.length > 0;
    const hasMeaningfulText = node.textContent?.trim() !== '';

    if (!hasChildElements && !hasMeaningfulText) {
      node.parentNode?.removeChild(node);
    }
  }

  traverseAndClean(body);

  return body.innerHTML;
}

function getPageContent() {
  const content = [...document.querySelectorAll('[data-help-helpful-content]')]
    .map((element) => element.innerHTML)
    .join('');

  return cleanHtml(content);
}

export class HelpSession {
  name?: string;

  topics: HelpSessionTopic[] = [];

  currentTopicName?: string;

  prefixId?: string;

  start(name: string) {
    this.name = name;
    this.prefixId = Math.random().toString(36).substring(2, 8);
    this.topics = [];
    this.currentTopicName = undefined;
  }

  end() {
    this.name = undefined;
    this.prefixId = undefined;
    this.topics = [];
    this.currentTopicName = undefined;
  }

  hasStarted() {
    return Boolean(this.name);
  }

  updateCurrentPageContent() {
    this.setValue('pageContent', getPageContent());
  }

  setCurrentTopicName(topicName: string) {
    if (!this.hasStarted()) {
      throw new Error('Help session not started');
    }

    let topic = this.getTopicByName(topicName);

    if (!topic) {
      topic = new HelpSessionTopic(topicName);
      this.topics.push(topic);
    }

    this.currentTopicName = topicName;

    return topic;
  }

  getCurrentTopic() {
    if (!this.currentTopicName) {
      return undefined;
    }

    const currentTopic = this.topics.find((t) => t.name === this.currentTopicName);

    return currentTopic;
  }

  private getTopicByName(name: string) {
    return this.topics.find((t) => t.name === name);
  }

  setValue(name: string, value: string) {
    const topic = this.getCurrentTopic();
    if (!topic) {
      throw new Error('No current topic set');
    }
    topic.setValue(name, value);
  }

  getCurrentTopicAsPromptString() {
    const topic = this.getCurrentTopic();
    if (!topic) {
      return '';
    }

    const values = topic.getValues();
    return Object.entries(values)
      .map(([key, value]) => `[[${key}]]\n${value}`)
      .join('\n\n');
  }

  getCurrentId() {
    const topic = this.getCurrentTopic();
    if (!topic) {
      throw new Error('No current topic set');
    }
    return `${this.prefixId}-${this.name}-${topic.getId()}`;
  }

  async getThreadId() {
    if (this.name === undefined) {
      return undefined;
    }

    const threadIds = (await localStorage.getItem<ThreadIdList>(STORAGE_KEY)) || {};
    if (!threadIds?.[this.name!]) {
      return undefined;
    }

    return threadIds[this.name!];
  }

  async setThreadId(threadId: number) {
    const threadIds = await localStorage.getItem<ThreadIdList>(STORAGE_KEY);
    localStorage.setItem(STORAGE_KEY, {
      ...threadIds,
      [this.name!]: threadId,
    });
  }
}

const serviceInstance = new HelpSession();

export default serviceInstance;
